// src/features/videos/VideosPage.tsx — placeholder Videos page (web).
import { useMemo } from 'react';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { useVideosHome } from './real/useRealVideos';
import { RealVideosPage } from './real/RealVideosPage';
import { useVideos, useFollowedHandles } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { VideoChips } from './VideoChips';
import { VideoCard } from './VideoCard';
import { RankedVideoCard } from './RankedVideoCard';
import { FeaturedVideoHero } from './FeaturedVideoHero';
import { TopCreatorsPanel } from './panels/TopCreatorsPanel';
import { VideoCategoriesPanel } from './panels/VideoCategoriesPanel';
import { ShortsPanel } from './panels/ShortsPanel';
import { filterVideos } from './filterVideos';
import { WEB_CHIPS, PREVIEW_NOTICE } from './meta';
import { PreviewNotice } from '@/components/PreviewNotice';
import type { VideoChipId, VideoCreator } from '@/core/models/video';

const VALID_CHIP_IDS = new Set<string>(WEB_CHIPS.map((c) => c.id));

/** Validates the ?chip= query param against the known chip list — an unknown or
 *  missing value (e.g. `?chip=bogus`) falls back to 'for-you' instead of
 *  half-selecting nothing. */
function normalizeChip(raw: string | null): VideoChipId {
  return raw !== null && VALID_CHIP_IDS.has(raw) ? (raw as VideoChipId) : 'for-you';
}

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SectionHeading = styled.h2`
  margin: 22px 0 12px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;

const RankedRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

function creatorMap(creators: VideoCreator[]): Record<string, VideoCreator> {
  const map: Record<string, VideoCreator> = {};
  for (const c of creators) map[c.id] = c;
  return map;
}

// The Videos route is the Videos page. The `external_media` network family (video playlists)
// used to take over this whole route when its flag was on — on the live site that hid the
// real Videos page behind "Video playlists" (2026-09-26). Playlists keep their own page at
// /playlists, reached from the NetworkLinks shortcut both views render.
export function VideosPage() {
  return <VideosHomeGate />;
}

/** Real videos once any exist; until then the labelled samples (owner decision). */
function VideosHomeGate() {
  const home = useVideosHome();
  if (home.state === 'loading') {
    return <Empty role="status">Loading videos…</Empty>;
  }
  if (home.state === 'ready' && home.data.hasVideos) return <RealVideosPage home={home.data} />;
  return <VideosPreview />;
}

function VideosPreview() {
  // The URL is the single source of truth for the active chip — no local state
  // to fall out of sync with it. A prior version read searchParams only inside a
  // useState initializer, so any URL change after mount (e.g. clicking a
  // VideoCategoriesPanel link while already on /videos) was silently ignored.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeChip = normalizeChip(searchParams.get('chip'));
  const seed = useVideos();
  // Every seed item is a Sample (owner: keep samples, clearly labelled, until real videos
  // exist) — the cards' VideoThumb draws the badge from this flag.
  const videos = useMemo(() => seed.videos.map((v) => ({ ...v, sample: true })), [seed.videos]);
  const shorts = useMemo(() => seed.shorts.map((v) => ({ ...v, sample: true })), [seed.shorts]);
  const { creators, loaded } = seed;
  const followedHandles = useFollowedHandles();

  function handleChipChange(id: VideoChipId) {
    setSearchParams(id === 'for-you' ? {} : { chip: id });
  }

  const byId = useMemo(() => creatorMap(creators), [creators]);
  const creatorHandles = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of creators) map[c.id] = c.handle;
    return map;
  }, [creators]);

  const longform = useMemo(() => videos.filter((v) => !v.short), [videos]);
  // The featured hero only makes sense as a "for you" recommendation — showing
  // it fixed regardless of the active chip (e.g. a news video headlining while
  // Music is selected) read as broken, not curated.
  const featured = useMemo(
    () =>
      activeChip === 'for-you' ? (longform.find((v) => v.featured) ?? longform[0]) : undefined,
    [longform, activeChip],
  );

  const visible = useMemo(
    () =>
      filterVideos(longform, { chip: activeChip, followedHandles, creatorHandles }).filter(
        (v) => v.id !== featured?.id,
      ),
    [longform, activeChip, followedHandles, creatorHandles, featured],
  );

  const forYou = visible.slice(0, 4);
  const trending = useMemo(
    () =>
      longform
        .filter((v) => v.trendingRank !== undefined)
        .sort((a, b) => (a.trendingRank ?? 0) - (b.trendingRank ?? 0))
        .slice(0, 5),
    [longform],
  );

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="videos">
        <RailPanel id="creators" label="Top creators">
          <TopCreatorsPanel creators={creators} />
        </RailPanel>
        <RailPanel id="categories" label="Video categories">
          <VideoCategoriesPanel />
        </RailPanel>
        <RailPanel id="shorts" label="Flash">
          <ShortsPanel shorts={shorts} />
        </RailPanel>
      </RailGroup>
    ),
    [creators, shorts],
  );
  useRightRail(rightRail);

  return (
    <section>
      <NetworkLinks destination="videos" />
      <Header>
        <HeaderText>
          <Title>Videos</Title>
          <Subtitle>Watch. Share. Speak freely.</Subtitle>
        </HeaderText>
      </Header>

      <PreviewNotice text={PREVIEW_NOTICE} />

      <VideoChips chips={WEB_CHIPS} active={activeChip} onChange={handleChipChange} />

      {loaded && featured && (
        <FeaturedVideoHero video={featured} creator={byId[featured.authorId]} />
      )}

      <SectionHeading>For you</SectionHeading>
      {forYou.length === 0 ? (
        <Empty>Nothing here yet.</Empty>
      ) : (
        <Grid data-testid="for-you-row">
          {forYou.map((v) => (
            <VideoCard key={v.id} video={v} creator={byId[v.authorId]} />
          ))}
        </Grid>
      )}

      <SectionHeading>Trending now</SectionHeading>
      {trending.length === 0 ? (
        <Empty>Nothing trending yet.</Empty>
      ) : (
        <RankedRow data-testid="trending-row">
          {trending.map((v, i) => (
            <RankedVideoCard key={v.id} video={v} rank={i + 1} creator={byId[v.authorId]} />
          ))}
        </RankedRow>
      )}
    </section>
  );
}
